import { RulerLine } from "./models/RulerLine";
import type { RulersLayoutGrid } from "./models/RulersLayoutGrid";
import { SnapCandidate, SnapEngine, SnapResult } from "./SnapEngine";
import { RULER_MODES, type RulerMode, type FixedGuideLine } from "./models/RulerMode";
import { StringDevViewportLayer } from "../core/StringDevViewportLayer";
import { startPointerDrag } from "../core/startPointerDrag";
import { getStringDevViewportPolicy } from "../core/StringDevViewportPolicy";
import { lockPageInteraction } from "../../../utils/interaction-lock";

const RULER_THICKNESS = 20; // px — width of horizontal / vertical ruler bar
const RULER_THICKNESS_MOBILE = 12;
const RULER_PLATE_BACKGROUND_COLOR = "#E3E3E3";
const RULER_PLATE_BACKGROUND_OPACITY = 0.85;
const RULER_PLATE_BORDER_COLOR = "#000000";
const RULER_PLATE_BORDER_OPACITY = 0.25;
const RULER_TICK_COLOR = "#313131";
const RULER_TICK_FONT_SIZE = 10;
const RULER_TICK_FONT = `${RULER_TICK_FONT_SIZE}px "JetBrains Mono", monospace`;
const RULER_TICK_LABEL_OFFSET_TOP = 4;
const RULER_TICK_LABEL_OFFSET_LEFT = -2;
const RULER_TICK_LONG_SIZE = 8;
const RULER_TICK_SHORT_SIZE = 4;
const RULER_TICK_LONG_SIZE_MOBILE = 5;
const RULER_TICK_SHORT_SIZE_MOBILE = 2;

const COL_EDGE_COLOR = "rgba(255, 56, 96, 0.50)";
const COL_CENTER_COLOR = "rgba(255, 56, 96, 0.22)";
const COL_BG_COLOR = "rgba(255, 56, 96, 0.05)";
const COL_LABEL_COLOR = "rgba(255, 56, 96, 0.45)";
const COL_LABEL_FONT_SIZE = 8;
const COL_LABEL_FONT = `${COL_LABEL_FONT_SIZE}px "JetBrains Mono", monospace`;

const ROW_EDGE_COLOR = "rgba(0, 120, 255, 0.50)";
const ROW_CENTER_COLOR = "rgba(0, 120, 255, 0.22)";
const ROW_BG_COLOR = "rgba(0, 120, 255, 0.05)";

function fillRectWithAlpha(
  ctx: CanvasRenderingContext2D,
  color: string,
  alpha: number,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.fillRect(x, y, width, height);
  ctx.restore();
}

export interface RulersColumnLayout {
  columns: number;
  margin: string;
  gap: string;
}

export interface RulersRowLayout {
  /** Height of each row band (CSS unit value: px, rem, %, vw, vh). */
  size: string;
  /** Gap between row bands (CSS unit value). */
  gap: string;
}

function resolveCSSUnit(
  value: string,
  axis: "x" | "y" = "x",
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight,
): number {
  const match = value.trim().match(/^(-?[\d.]+)\s*(px|rem|%|vw|vh)?$/);
  if (!match) return 0;
  const num = parseFloat(match[1]);
  const unit = match[2] || "px";
  switch (unit) {
    case "px":
      return num;
    case "rem":
      return num * parseFloat(getComputedStyle(document.documentElement).fontSize);
    case "%":
      return axis === "y" ? (num / 100) * viewportHeight : (num / 100) * viewportWidth;
    case "vw":
      return (num / 100) * viewportWidth;
    case "vh":
      return (num / 100) * viewportHeight;
    default:
      return num;
  }
}

type DragCommitCallback = (
  lineId: string,
  position: number,
  targetLabel?: string,
  targetSelector?: string,
  targetEdge?: RulerLine["targetEdge"],
) => void;
type LineCreatedCallback = (
  axis: "horizontal" | "vertical",
  position: number,
  targetLabel?: string,
  targetSelector?: string,
  targetEdge?: RulerLine["targetEdge"],
) => void;
type LineMovedCallback = (lineId: string, position: number, targetLabel?: string) => void;
type LineRemovedCallback = (lineId: string) => void;

export interface RulersOverlayCallbacks {
  onLineCreated: LineCreatedCallback;
  onLineMoved: LineMovedCallback;
  onLineRemoved: LineRemovedCallback;
  onDragCommit: DragCommitCallback;
  onModeToggle: () => void;
}

export interface RulersOverlayOptions {
  /** Grid step in document pixels. 0 / 1 = disabled. */
  snapStep: number;
  /** Whether to snap to element edges and centers during drag. Default: true. */
  snapToElements: boolean;
  /** Pixel threshold for snapping. Default: 8. */
  snapThreshold: number;
  /** CSS selector for snap targets. Default: "[string],[data-string]". */
  snapSelector: string;
  /** Layout grid definitions (columns / rows) rendered as overlay bands. */
  layoutGrids?: RulersLayoutGrid[];
  /** Column layout with CSS-unit margins, column count, and gap. */
  columnLayout?: RulersColumnLayout;
  /** Row layout with CSS-unit row size and gap. */
  rowLayout?: RulersRowLayout;
  /** Scroll element to lock while dragging rulers on mobile. */
  scrollTarget?: HTMLElement | null;
}

const DEFAULT_OPTIONS: RulersOverlayOptions = {
  snapStep: 0,
  snapToElements: true,
  snapThreshold: 8,
  snapSelector: "[string],[data-string]",
};

const COLUMNS_COLOR_DEFAULT = "rgba(255, 0, 80, 0.08)";
const ROWS_COLOR_DEFAULT = "rgba(0, 120, 255, 0.06)";

/**
 * Global fixed-position overlay that renders ruler bars, layout grid, and guide lines.
 *
 * Layer order (bottom → top):
 *   [data-string-rulers-grid]   z 9998  layout-grid canvas (columns / rows)
 *   [data-string-rulers-top]    z 10001 horizontal ruler bar
 *   [data-string-rulers-left]   z 10001 vertical ruler bar
 *   [data-string-rulers-lines]  z 10000 guide line DOM nodes
 *
 * Snap feedback:
 *   amber  →  snapping to a DOM element edge / center
 *   green  →  snapping to a layout grid line
 */
export class RulersOverlay {
  private viewportLayer: StringDevViewportLayer;
  private host: HTMLElement | null;
  private gridCanvas: HTMLCanvasElement;
  private topCanvas: HTMLCanvasElement;
  private leftCanvas: HTMLCanvasElement;
  private cornerEl: HTMLDivElement;
  private cornerLabelEl: HTMLSpanElement;
  private linesLayer: HTMLDivElement;
  private gapIndicatorEl: HTMLDivElement;
  private gapIndicatorLabelEl: HTMLSpanElement;

  // DOM nodes for active guide lines: lineId → { el, labelEl }
  private lineEls: Map<string, { el: HTMLDivElement; labelEl: HTMLSpanElement }> = new Map();

  // DOM nodes for mode-provided fixed guide lines (e.g. center cross)
  private fixedLineEls: Map<string, HTMLDivElement> = new Map();

  private callbacks: RulersOverlayCallbacks;
  private options: RulersOverlayOptions;
  private snapEngine: SnapEngine;
  private resizeObserver: ResizeObserver;
  private currentMode: RulerMode = RULER_MODES.default;

  private visible: boolean = false;
  private viewportWidth: number = window.innerWidth;
  private viewportHeight: number = window.innerHeight;
  private contentWidth: number = document.documentElement.scrollWidth;
  private contentHeight: number = document.documentElement.scrollHeight;
  private viewportOffsetX: number = 0;
  private viewportOffsetY: number = 0;

  // Current scroll offset (updated every frame from StringDevRulers)
  private scrollX: number = 0;
  private scrollY: number = 0;

  // Last pixel-rounded values used for canvas redraw (avoid redundant redraws)
  private renderedScrollX: number = -1;
  private renderedScrollY: number = -1;

  // Last rendered lines
  private currentLines: RulerLine[] = [];

  // Currently highlighted snap-target element
  private _snapHighlightEl: Element | null = null;
  private _guideReferenceLineIds: [string, string] | null = null;
  private _guideReferenceAxis: "horizontal" | "vertical" | null = null;
  private _guideReferenceGap: number | null = null;
  private dragInteractionReleases = new Set<() => void>();

  constructor(
    callbacks: RulersOverlayCallbacks,
    options: Partial<RulersOverlayOptions> = {},
    host: HTMLElement | undefined,
    viewportLayer: StringDevViewportLayer,
  ) {
    this.callbacks = callbacks;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.host = host ?? null;
    this.viewportLayer = viewportLayer;

    this.snapEngine = new SnapEngine({
      threshold: this.options.snapThreshold,
      snapStep: this.options.snapStep,
      snapToElements: this.options.snapToElements,
      snapSelector: this.options.snapSelector,
      layoutGrids: this.options.layoutGrids,
    });

    this.gridCanvas = this.createCanvas("grid");
    this.topCanvas = this.createCanvas("top");
    this.leftCanvas = this.createCanvas("left");
    this.linesLayer = this.createLinesLayer();
    this.gapIndicatorEl = this.createGapIndicator();
    this.gapIndicatorLabelEl = document.createElement("span");
    this.gapIndicatorLabelEl.setAttribute("data-string-rulers-gap-indicator-label", "");
    this.gapIndicatorLabelEl.setAttribute("data-stdg", "");

    this.gapIndicatorEl.appendChild(this.gapIndicatorLabelEl);

    this.cornerEl = document.createElement("div");
    this.cornerEl.setAttribute("data-string-rulers-corner", "");
    this.cornerEl.setAttribute("data-mode", this.currentMode.id);
    this.cornerLabelEl = document.createElement("span");
    this.cornerLabelEl.textContent = this.currentMode.cornerLabel;
    this.cornerEl.appendChild(this.cornerLabelEl);
    this.cornerEl.addEventListener("click", () => this.callbacks.onModeToggle());

    const screenRoot = this.viewportLayer.getScreenElement();
    const worldRoot = this.viewportLayer.getWorldElement(host);

    worldRoot.appendChild(this.gridCanvas);
    worldRoot.appendChild(this.linesLayer);
    this.linesLayer.appendChild(this.gapIndicatorEl);
    screenRoot.appendChild(this.topCanvas);
    screenRoot.appendChild(this.leftCanvas);
    screenRoot.appendChild(this.cornerEl);

    document.body.setAttribute("data-string-rulers-hidden", "");

    this.resizeObserver = new ResizeObserver(() => {
      if (this.visible) this.redraw();
    });
    this.resizeObserver.observe(document.documentElement);

    this.bindRulerDrag(this.topCanvas, "horizontal");
    this.bindRulerDrag(this.leftCanvas, "vertical");
  }

  // ── Public API ────────────────────────────────────────

  refresh(lines: RulerLine[]): void {
    this.currentLines = lines;
    this.redraw();
  }

  updateScroll(
    scrollX: number,
    scrollY: number,
    viewportWidth: number,
    viewportHeight: number,
    contentWidth: number,
    contentHeight: number,
  ): void {
    const metricsChanged =
      this.viewportWidth !== viewportWidth ||
      this.viewportHeight !== viewportHeight ||
      this.contentWidth !== contentWidth ||
      this.contentHeight !== contentHeight;

    this.scrollX = scrollX;
    this.scrollY = scrollY;
    this.viewportWidth = viewportWidth;
    this.viewportHeight = viewportHeight;
    this.contentWidth = contentWidth;
    this.contentHeight = contentHeight;
    this.syncScreenChromePosition();
    if (!this.visible) return;

    const rx = Math.round(scrollX);
    const ry = Math.round(scrollY);
    const canvasNeedsRedraw =
      metricsChanged || rx !== this.renderedScrollX || ry !== this.renderedScrollY;

    if (canvasNeedsRedraw) {
      this.renderedScrollX = rx;
      this.renderedScrollY = ry;
      if (metricsChanged) {
        this.syncWorldDimensions();
      }
      this.drawTopRuler(this.viewportWidth);
      this.drawLeftRuler(this.viewportHeight);
      if (metricsChanged) {
        this.drawGrid(this.contentWidth, this.contentHeight);
      }
    }

    this.updateLineLabels();
    this._positionGuideReferenceIndicator();

    // Fixed lines only exist in non-default modes — skip entirely when empty
    if (this.fixedLineEls.size > 0 || this.currentMode.id !== "default") {
      this.syncFixedLines();
    }
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (visible) {
      document.body.removeAttribute("data-string-rulers-hidden");
      this.redraw();
    } else {
      document.body.setAttribute("data-string-rulers-hidden", "");
    }
  }

  setMode(mode: RulerMode): void {
    this.currentMode = mode;
    this.cornerEl.setAttribute("data-mode", mode.id);
    this.cornerLabelEl.textContent = mode.cornerLabel;
    this.redraw();
  }

  destroy(): void {
    this.releaseAllDragInteractionLocks();
    this._setSnapHighlight(null);
    this._setGuideReference(null, null, null);
    this.resizeObserver.disconnect();
    this.gridCanvas.remove();
    this.topCanvas.remove();
    this.leftCanvas.remove();
    this.cornerEl.remove();
    this.linesLayer.remove();
    document.body.removeAttribute("data-string-rulers-hidden");
    this.lineEls.clear();
    for (const [, el] of this.fixedLineEls) el.remove();
    this.fixedLineEls.clear();
  }

  // ── Internal redraw ───────────────────────────────────

  private redraw(): void {
    this.renderedScrollX = Math.round(this.scrollX);
    this.renderedScrollY = Math.round(this.scrollY);
    this.syncScreenChromePosition();
    this.syncWorldDimensions();
    this.drawTopRuler(this.viewportWidth);
    this.drawLeftRuler(this.viewportHeight);
    this.drawGrid(this.contentWidth, this.contentHeight);
    this.syncLineEls(this.currentLines);
    this.syncFixedLines();
    this._positionGuideReferenceIndicator();
  }

  private updateLineLabels(): void {
    for (const line of this.currentLines) {
      const entry = this.lineEls.get(line.id);
      if (!entry) continue;
      this.updateLineLabel(line, entry.labelEl);
    }
  }

  // ── Canvas creation ───────────────────────────────────

  private createCanvas(side: "grid" | "top" | "left"): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.setAttribute(`data-string-rulers-${side}`, "");
    return canvas;
  }

  private createLinesLayer(): HTMLDivElement {
    const div = document.createElement("div");
    div.setAttribute("data-string-rulers-lines", "");
    return div;
  }

  private createGapIndicator(): HTMLDivElement {
    const el = document.createElement("div");
    el.setAttribute("data-string-rulers-gap-indicator", "");
    el.setAttribute("hidden", "");
    return el;
  }

  private syncWorldDimensions(): void {
    this.linesLayer.style.width = `${this.contentWidth}px`;
    this.linesLayer.style.height = `${this.contentHeight}px`;
    this.gridCanvas.style.width = `${this.contentWidth}px`;
    this.gridCanvas.style.height = `${this.contentHeight}px`;
  }

  // ── Layout grid drawing ───────────────────────────────

  private drawGrid(worldWidth: number, worldHeight: number): void {
    const grids = this.options.layoutGrids;
    const c = this.gridCanvas;
    if (c.width !== worldWidth || c.height !== worldHeight) {
      c.width = worldWidth;
      c.height = worldHeight;
      c.style.width = `${worldWidth}px`;
      c.style.height = `${worldHeight}px`;
    }

    if (!grids?.length) return;

    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, worldWidth, worldHeight);

    for (const grid of grids) {
      if (grid.type === "columns")
        this._drawColumnsGrid(ctx, this.viewportWidth, worldHeight, grid);
      if (grid.type === "rows") this._drawRowsGrid(ctx, this.viewportWidth, worldHeight, grid);
    }
  }

  private _drawColumnsGrid(
    ctx: CanvasRenderingContext2D,
    vw: number,
    vh: number,
    grid: import("./models/RulersLayoutGrid").ColumnsLayoutGrid,
  ): void {
    const { count, marginLeft = 0, marginRight = 0, gap = 0 } = grid;
    const usableWidth = vw - marginLeft - marginRight;
    if (usableWidth <= 0 || count <= 0) return;

    const colWidth = (usableWidth - (count - 1) * gap) / count;
    ctx.fillStyle = grid.color ?? COLUMNS_COLOR_DEFAULT;

    for (let i = 0; i < count; i++) {
      const x = marginLeft + i * (colWidth + gap);
      ctx.fillRect(x, 0, colWidth, vh);
    }
  }

  private _drawRowsGrid(
    ctx: CanvasRenderingContext2D,
    vw: number,
    vh: number,
    grid: import("./models/RulersLayoutGrid").RowsLayoutGrid,
  ): void {
    const { height, gap = 0 } = grid;
    const period = height + gap;
    if (period <= 0 || height <= 0) return;

    ctx.fillStyle = grid.color ?? ROWS_COLOR_DEFAULT;
    for (let y = 0; y < vh; y += period) {
      ctx.fillRect(0, y, vw, height);
    }
  }

  // ── Ruler drawing ─────────────────────────────────────

  private drawTopRuler(vw: number): void {
    const t = this.getRulerThickness();
    const c = this.topCanvas;
    // Only resize canvas when dimensions change (avoids buffer reallocation)
    if (c.width !== vw || c.height !== t) {
      c.width = vw;
      c.height = t;
    }
    const ctx = c.getContext("2d")!;

    ctx.clearRect(0, 0, vw, t);
    fillRectWithAlpha(
      ctx,
      RULER_PLATE_BACKGROUND_COLOR,
      RULER_PLATE_BACKGROUND_OPACITY,
      0,
      0,
      vw,
      t,
    );
    fillRectWithAlpha(ctx, RULER_PLATE_BORDER_COLOR, RULER_PLATE_BORDER_OPACITY, 0, t - 1, vw, 1);

    this._drawColumnMarkers(ctx, vw, t);
    this.drawTicks(ctx, vw, t, "top", this.scrollX);
  }

  private drawLeftRuler(vh: number): void {
    const t = this.getRulerThickness();
    const c = this.leftCanvas;
    if (c.width !== t || c.height !== vh) {
      c.width = t;
      c.height = vh;
    }
    const ctx = c.getContext("2d")!;

    ctx.clearRect(0, 0, t, vh);
    fillRectWithAlpha(
      ctx,
      RULER_PLATE_BACKGROUND_COLOR,
      RULER_PLATE_BACKGROUND_OPACITY,
      0,
      0,
      t,
      vh,
    );
    fillRectWithAlpha(ctx, RULER_PLATE_BORDER_COLOR, RULER_PLATE_BORDER_OPACITY, t - 1, 0, 1, vh);

    this._drawRowMarkers(ctx, t, vh);
    this.drawTicks(ctx, vh, t, "left", this.scrollY);
  }

  private drawTicks(
    ctx: CanvasRenderingContext2D,
    length: number,
    thickness: number,
    side: "top" | "left",
    offset: number,
  ): void {
    const { tick: tickStep, label: labelInterval } = this.pickStep(length);
    const axis: "horizontal" | "vertical" = side === "top" ? "vertical" : "horizontal";
    const isTop = side === "top";
    const compact = this.isCompactMobileRuler();
    const showTickLabels = !compact;
    const halfThickness = Math.round(thickness / 2);
    const longTick = Math.min(
      thickness,
      compact ? RULER_TICK_LONG_SIZE_MOBILE : RULER_TICK_LONG_SIZE,
    );
    const shortTick = Math.min(
      thickness,
      compact ? RULER_TICK_SHORT_SIZE_MOBILE : RULER_TICK_SHORT_SIZE,
    );

    ctx.fillStyle = RULER_TICK_COLOR;
    ctx.font = RULER_TICK_FONT;

    const startDoc = Math.ceil(offset / tickStep) * tickStep;
    const startScreen = startDoc - offset;

    if (isTop) {
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      for (let i = 0; startScreen + i * tickStep <= length; i++) {
        const screenPos = Math.round(startScreen + i * tickStep);
        const docPos = startDoc + i * tickStep;
        const displayValue = this.currentMode.formatPosition(docPos, axis, length, offset);
        const showLabel = Math.round(displayValue) % labelInterval === 0;
        const tickLen = showLabel ? longTick : shortTick;

        const tickStart = compact ? 0 : thickness - tickLen;
        ctx.fillRect(screenPos, tickStart, 1, tickLen);
        if (showTickLabels && showLabel && screenPos > 4) {
          ctx.fillText(String(Math.round(displayValue)), screenPos, RULER_TICK_LABEL_OFFSET_TOP);
        }
      }
    } else {
      // Left ruler — labels are rotated, need save/restore per label
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (let i = 0; startScreen + i * tickStep <= length; i++) {
        const screenPos = Math.round(startScreen + i * tickStep);
        const docPos = startDoc + i * tickStep;
        const displayValue = this.currentMode.formatPosition(docPos, axis, length, offset);
        const showLabel = Math.round(displayValue) % labelInterval === 0;
        const tickLen = showLabel ? longTick : shortTick;

        const tickStart = compact ? 0 : thickness - tickLen;
        ctx.fillRect(tickStart, screenPos, tickLen, 1);
        if (showTickLabels && showLabel && screenPos > 4) {
          ctx.save();
          ctx.translate(halfThickness, screenPos);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText(String(Math.round(displayValue)), 0, RULER_TICK_LABEL_OFFSET_LEFT);
          ctx.restore();
        }
      }
    }
  }

  /**
   * Returns the tick step and the label interval for a given ruler length.
   * Label interval is always a clean multiple so labels land on round numbers
   * (e.g. step=25 → labels at 0, 100, 200 — not 0, 125, 250).
   */
  private pickStep(length: number): { tick: number; label: number } {
    if (length < 300) return { tick: 5, label: 25 };
    if (length < 800) return { tick: 10, label: 50 };
    if (length < 1800) return { tick: 25, label: 100 };
    return { tick: 50, label: 500 };
  }

  // ── Column layout ────────────────────────────────────

  private _resolveColumnLayout(): {
    columns: number;
    marginPx: number;
    gapPx: number;
    colWidth: number;
  } | null {
    const cl = this.options.columnLayout;
    if (!cl || cl.columns <= 0) return null;
    const marginPx = resolveCSSUnit(cl.margin, "x", this.viewportWidth, this.viewportHeight);
    const gapPx = resolveCSSUnit(cl.gap, "x", this.viewportWidth, this.viewportHeight);
    const vw = this.viewportWidth;
    const usableWidth = vw - 2 * marginPx;
    if (usableWidth <= 0) return null;
    const colWidth = (usableWidth - (cl.columns - 1) * gapPx) / cl.columns;
    if (colWidth <= 0) return null;
    return { columns: cl.columns, marginPx, gapPx, colWidth };
  }

  private _drawColumnMarkers(ctx: CanvasRenderingContext2D, vw: number, thickness: number): void {
    const layout = this._resolveColumnLayout();
    if (!layout) return;
    const { columns, marginPx, gapPx, colWidth } = layout;

    // Subtle column backgrounds on the ruler
    ctx.fillStyle = COL_BG_COLOR;
    for (let i = 0; i < columns; i++) {
      const x = marginPx + i * (colWidth + gapPx);
      ctx.fillRect(x, 0, colWidth, thickness);
    }

    // Column edge lines (full ruler height)
    ctx.fillStyle = COL_EDGE_COLOR;
    for (let i = 0; i < columns; i++) {
      const left = marginPx + i * (colWidth + gapPx);
      const right = left + colWidth;
      ctx.fillRect(Math.round(left), 0, 1, thickness);
      ctx.fillRect(Math.round(right), 0, 1, thickness);
    }

    // Column center marks (shorter)
    ctx.fillStyle = COL_CENTER_COLOR;
    for (let i = 0; i < columns; i++) {
      const left = marginPx + i * (colWidth + gapPx);
      const center = left + colWidth / 2;
      ctx.fillRect(Math.round(center), Math.round(thickness * 0.6), 1, Math.round(thickness * 0.4));
    }

    // Column number labels (only if columns are wide enough)
    if (!this.isCompactMobileRuler() && colWidth > 16) {
      ctx.fillStyle = COL_LABEL_COLOR;
      ctx.font = COL_LABEL_FONT;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (let i = 0; i < columns; i++) {
        const left = marginPx + i * (colWidth + gapPx);
        const center = left + colWidth / 2;
        ctx.fillText(String(i + 1), Math.round(center), Math.round(thickness / 2));
      }
    }
  }

  private _buildColumnCandidates(axis: "horizontal" | "vertical"): SnapCandidate[] {
    if (axis !== "vertical") return [];
    const layout = this._resolveColumnLayout();
    if (!layout) return [];
    const { columns, marginPx, gapPx, colWidth } = layout;
    const candidates: SnapCandidate[] = [];

    for (let i = 0; i < columns; i++) {
      const left = marginPx + i * (colWidth + gapPx);
      const right = left + colWidth;
      const center = (left + right) / 2;
      const n = i + 1;

      candidates.push({
        position: left + this.scrollX,
        type: "grid-column-edge",
        label: `c${n}`,
      });
      candidates.push({
        position: right + this.scrollX,
        type: "grid-column-edge",
        label: `c${n}◂`,
      });
      candidates.push({
        position: center + this.scrollX,
        type: "grid-column-center",
        label: `c${n}⊙`,
      });
    }

    return candidates;
  }

  // ── Row layout ───────────────────────────────────────

  private _resolveRowLayout(): { sizePx: number; gapPx: number; period: number } | null {
    const rl = this.options.rowLayout;
    if (!rl) return null;
    const sizePx = resolveCSSUnit(rl.size, "y", this.viewportWidth, this.viewportHeight);
    const gapPx = resolveCSSUnit(rl.gap, "y", this.viewportWidth, this.viewportHeight);
    if (sizePx <= 0) return null;
    return { sizePx, gapPx, period: sizePx + gapPx };
  }

  private _drawRowMarkers(ctx: CanvasRenderingContext2D, thickness: number, vh: number): void {
    const layout = this._resolveRowLayout();
    if (!layout) return;
    const { sizePx, gapPx, period } = layout;

    const startDoc = Math.floor(this.scrollY / period) * period;
    const endDoc = this.scrollY + vh + period;

    for (let docY = startDoc; docY < endDoc; docY += period) {
      const topScreen = Math.round(docY - this.scrollY);
      const bottomScreen = Math.round(docY + sizePx - this.scrollY);

      // Row background band
      const bgTop = Math.max(0, topScreen);
      const bgBottom = Math.min(vh, bottomScreen);
      if (bgBottom > bgTop) {
        ctx.fillStyle = ROW_BG_COLOR;
        ctx.fillRect(0, bgTop, thickness, bgBottom - bgTop);
      }

      // Top edge line
      if (topScreen >= 0 && topScreen <= vh) {
        ctx.fillStyle = ROW_EDGE_COLOR;
        ctx.fillRect(0, topScreen, thickness, 1);
      }

      // Bottom edge line (only when there's a gap, marking end of band)
      if (gapPx > 0 && bottomScreen >= 0 && bottomScreen <= vh) {
        ctx.fillStyle = ROW_EDGE_COLOR;
        ctx.fillRect(0, bottomScreen, thickness, 1);
      }

      // Center mark (shorter, from left edge)
      if (sizePx > 4) {
        const centerScreen = Math.round(docY + sizePx / 2 - this.scrollY);
        if (centerScreen >= 0 && centerScreen <= vh) {
          ctx.fillStyle = ROW_CENTER_COLOR;
          ctx.fillRect(0, centerScreen, Math.round(thickness * 0.45), 1);
        }
      }
    }
  }

  private _buildRowCandidates(axis: "horizontal" | "vertical"): SnapCandidate[] {
    if (axis !== "horizontal") return [];
    const layout = this._resolveRowLayout();
    if (!layout) return [];
    const { sizePx, gapPx, period } = layout;

    const vh = this.viewportHeight;
    const rangeStart = Math.max(0, this.scrollY - 200);
    const rangeEnd = this.scrollY + vh + 200;
    const firstRow = Math.floor(rangeStart / period) * period;
    const candidates: SnapCandidate[] = [];

    for (let docY = firstRow; docY < rangeEnd; docY += period) {
      const n = Math.round(docY / period) + 1;

      candidates.push({ position: docY, type: "grid-row-edge", label: `r${n}` });
      candidates.push({ position: docY + sizePx / 2, type: "grid-row-edge", label: `r${n}⊙` });
      if (gapPx > 0) {
        candidates.push({ position: docY + sizePx, type: "grid-row-edge", label: `r${n}◂` });
      }
    }

    return candidates;
  }

  // ── Fixed (mode-provided) guide lines ────────────────

  private syncFixedLines(): void {
    const fixedLines = this.currentMode.getFixedLines(
      this.viewportWidth,
      this.viewportHeight,
      this.scrollX,
      this.scrollY,
    );

    // Build a set of keys we expect this frame
    const expectedKeys = new Set<string>();
    for (const fl of fixedLines) {
      const key = `fixed-${fl.axis}`;
      expectedKeys.add(key);

      let el = this.fixedLineEls.get(key);
      if (!el) {
        el = document.createElement("div");
        el.setAttribute("data-string-rulers-fixed-line", "");
        el.setAttribute("data-axis", fl.axis);
        this.linesLayer.appendChild(el);
        this.fixedLineEls.set(key, el);
      }

      if (fl.axis === "horizontal") {
        el.style.top = `${fl.position}px`;
        el.style.left = "0";
        el.style.width = `${this.contentWidth}px`;
      } else {
        el.style.left = `${fl.position}px`;
        el.style.top = "0";
        el.style.height = `${this.contentHeight}px`;
      }
    }

    // Remove stale fixed lines (e.g. mode switched away from center)
    for (const [key, el] of this.fixedLineEls) {
      if (!expectedKeys.has(key)) {
        el.remove();
        this.fixedLineEls.delete(key);
      }
    }
  }

  // ── Guide line DOM sync ───────────────────────────────

  private syncLineEls(lines: RulerLine[]): void {
    for (const [id] of this.lineEls) {
      if (!lines.some((l) => l.id === id)) {
        this.lineEls.get(id)!.el.remove();
        this.lineEls.delete(id);
      }
    }
    for (const line of lines) {
      if (!this.lineEls.has(line.id)) this.createLineEl(line);
      this.positionLineEl(line);
    }
  }

  private createLineEl(line: RulerLine): void {
    const el = document.createElement("div");
    el.setAttribute("data-string-rulers-line", line.id);
    el.setAttribute("data-axis", line.axis);
    el.setAttribute("data-stdg", "");

    const labelEl = document.createElement("span");
    labelEl.setAttribute("data-string-rulers-label", "");
    el.appendChild(labelEl);

    this.linesLayer.appendChild(el);
    this.lineEls.set(line.id, { el, labelEl });

    el.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
      if (e.button === 1) {
        e.preventDefault();
        this.callbacks.onLineRemoved(line.id);
        return;
      }
      this.startLineDrag(e, line);
    });
  }

  private positionLineEl(line: RulerLine): void {
    const entry = this.lineEls.get(line.id);
    if (!entry) return;
    const { el, labelEl } = entry;

    if (line.axis === "horizontal") {
      el.style.top = `${line.position}px`;
      el.style.left = "0";
      el.style.width = `${this.contentWidth}px`;
    } else {
      el.style.left = `${line.position}px`;
      el.style.top = "0";
      el.style.height = `${this.contentHeight}px`;
    }

    this.updateLineLabel(line, labelEl);
  }

  private updateLineLabel(line: RulerLine, labelEl: HTMLSpanElement): void {
    const vSize = line.axis === "horizontal" ? this.viewportHeight : this.viewportWidth;
    const scroll = line.axis === "horizontal" ? this.scrollY : this.scrollX;
    const displayPos = this.currentMode.formatPosition(line.position, line.axis, vSize, scroll);
    const positionLabel = String(Math.round(displayPos));
    const nextLabel = line.targetLabel ? `${positionLabel} ${line.targetLabel}` : positionLabel;
    if (labelEl.textContent !== nextLabel) {
      labelEl.textContent = nextLabel;
    }
  }

  private _setGuideReference(
    axis: "horizontal" | "vertical" | null,
    referenceLineIds: [string, string] | null,
    gap: number | null,
  ): void {
    if (this._guideReferenceLineIds) {
      for (const lineId of this._guideReferenceLineIds) {
        this.lineEls.get(lineId)?.el.removeAttribute("data-string-rulers-guide-ref");
      }
    }

    this._guideReferenceAxis = axis;
    this._guideReferenceLineIds = referenceLineIds;
    this._guideReferenceGap = gap;

    if (referenceLineIds) {
      for (const lineId of referenceLineIds) {
        this.lineEls.get(lineId)?.el.setAttribute("data-string-rulers-guide-ref", "");
      }
    }

    this._positionGuideReferenceIndicator();
  }

  private _positionGuideReferenceIndicator(): void {
    const refIds = this._guideReferenceLineIds;
    const axis = this._guideReferenceAxis;
    const gap = this._guideReferenceGap;

    if (!refIds || !axis || gap == null) {
      this.gapIndicatorEl.setAttribute("hidden", "");
      this.gapIndicatorEl.removeAttribute("data-axis");
      return;
    }

    const lines = refIds
      .map((lineId) => this.currentLines.find((line) => line.id === lineId))
      .filter((line): line is RulerLine => Boolean(line));

    if (lines.length !== 2) {
      this.gapIndicatorEl.setAttribute("hidden", "");
      this.gapIndicatorEl.removeAttribute("data-axis");
      return;
    }

    const start = Math.min(lines[0].position, lines[1].position);
    const end = Math.max(lines[0].position, lines[1].position);

    this.gapIndicatorEl.removeAttribute("hidden");
    this.gapIndicatorEl.setAttribute("data-axis", axis);
    this.gapIndicatorLabelEl.textContent = `${Math.round(gap)}px`;

    if (axis === "vertical") {
      const centerY = Math.round(
        this.scrollY + Math.max(this.getRulerThickness() + 32, this.viewportHeight / 2),
      );
      this.gapIndicatorEl.style.left = `${start}px`;
      this.gapIndicatorEl.style.top = `${centerY}px`;
      this.gapIndicatorEl.style.width = `${Math.max(0, end - start)}px`;
      this.gapIndicatorEl.style.height = "0";
    } else {
      const centerX = Math.round(
        this.scrollX + Math.max(this.getRulerThickness() + 32, this.viewportWidth / 2),
      );
      this.gapIndicatorEl.style.left = `${centerX}px`;
      this.gapIndicatorEl.style.top = `${start}px`;
      this.gapIndicatorEl.style.width = "0";
      this.gapIndicatorEl.style.height = `${Math.max(0, end - start)}px`;
    }
  }

  // ── Snap helpers ──────────────────────────────────────

  private _setSnapHighlight(el: Element | null): void {
    if (this._snapHighlightEl === el) return;
    this._snapHighlightEl?.removeAttribute("data-string-rulers-snap-target");
    this._snapHighlightEl = el;
    el?.setAttribute("data-string-rulers-snap-target", "");
  }

  /**
   * Returns "element" or "grid" for use as the `data-snapping` value.
   * CSS uses this to apply different colours for each category.
   */
  private _snapCategory(type: SnapResult["type"]): "element" | "grid" | "guide" | "column" {
    if (type === "guide-gap") return "guide";
    if (type === "grid-column-edge" || type === "grid-column-center") return "column";
    if (type === "grid-step" || type.startsWith("grid-")) return "grid";
    return "element";
  }

  private _snapLabel(result: SnapResult, docPos: number, axis: "horizontal" | "vertical"): string {
    const rawPos = result.position ?? docPos;
    const vSize = this.getViewportSizeForAxis(axis);
    const scroll = axis === "horizontal" ? this.scrollY : this.scrollX;
    const pos = String(Math.round(this.currentMode.formatPosition(rawPos, axis, vSize, scroll)));
    if (result.type === "guide-gap" && typeof result.gap === "number") {
      return `${pos} ${Math.round(result.gap)}px`;
    }
    if (result.label) return `${pos} ${result.label}`;
    if (result.el) return `${pos} ${this._elementLabel(result.el)}`;
    return pos;
  }

  private _elementLabel(el: Element): string {
    if (el.id) return `#${el.id}`;
    const cls = el.classList[0];
    if (cls) return `.${cls}`;
    const key = el.getAttribute("string") ?? el.getAttribute("data-string");
    if (key) return `[${key}]`;
    return el.tagName.toLowerCase();
  }

  /** Produces a CSS selector that can re-find `el` via querySelector. */
  private _elementSelector(el: Element): string | null {
    if (el.id) return `#${CSS.escape(el.id)}`;
    const sKey = el.getAttribute("string");
    if (sKey) return `[string="${CSS.escape(sKey)}"]`;
    const dsKey = el.getAttribute("data-string");
    if (dsKey) return `[data-string="${CSS.escape(dsKey)}"]`;
    if (el.classList[0]) return `.${CSS.escape(el.classList[0])}`;
    return null;
  }

  /**
   * Extracts binding info (label, selector, edge) from a snap result.
   * Handles both DOM-element snaps and column-layout snaps.
   */
  private _resolveBinding(snapResult: SnapResult | null): {
    targetLabel?: string;
    targetSelector?: string;
    targetEdge?: RulerLine["targetEdge"];
  } {
    if (!snapResult) return {};

    // DOM element binding
    if (snapResult.el) {
      let edge: RulerLine["targetEdge"];
      switch (snapResult.type) {
        case "element-top":
          edge = "top";
          break;
        case "element-bottom":
          edge = "bottom";
          break;
        case "element-center-y":
          edge = "center-y";
          break;
        case "element-left":
          edge = "left";
          break;
        case "element-right":
          edge = "right";
          break;
        case "element-center-x":
          edge = "center-x";
          break;
      }
      return {
        targetLabel: this._elementLabel(snapResult.el),
        targetSelector: this._elementSelector(snapResult.el) ?? undefined,
        targetEdge: edge,
      };
    }

    // Column layout binding (labels: c1, c1◂, c1⊙)
    if (
      (snapResult.type === "grid-column-edge" || snapResult.type === "grid-column-center") &&
      snapResult.label
    ) {
      const m = snapResult.label.match(/^c(\d+)(◂|⊙)?$/);
      if (m) {
        const colIndex = parseInt(m[1], 10) - 1;
        const edge: RulerLine["targetEdge"] =
          m[2] === "◂" ? "col-right" : m[2] === "⊙" ? "col-center" : "col-left";
        return {
          targetLabel: snapResult.label,
          targetSelector: `__col:${colIndex}`,
          targetEdge: edge,
        };
      }
    }

    // Non-bindable snap (grid step, row, guide gap, etc.) — just keep label
    if (snapResult.label) return { targetLabel: snapResult.label };
    return {};
  }

  /**
   * Recalculates positions of bound lines from current DOM / layout state.
   * Call before `refresh()` on resize so lines track their targets.
   */
  recalculateBindings(lines: RulerLine[]): void {
    for (const line of lines) {
      if (!line.targetSelector || !line.targetEdge) continue;

      // Column layout binding
      if (line.targetSelector.startsWith("__col:")) {
        const colIndex = parseInt(line.targetSelector.slice(6), 10);
        const layout = this._resolveColumnLayout();
        if (!layout || colIndex < 0 || colIndex >= layout.columns) continue;
        const left = layout.marginPx + colIndex * (layout.colWidth + layout.gapPx);
        const right = left + layout.colWidth;
        switch (line.targetEdge) {
          case "col-left":
            line.position = left + this.scrollX;
            break;
          case "col-right":
            line.position = right + this.scrollX;
            break;
          case "col-center":
            line.position = (left + right) / 2 + this.scrollX;
            break;
        }
        continue;
      }

      // DOM element binding
      const el = document.querySelector(line.targetSelector);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      switch (line.targetEdge) {
        case "top":
          line.position = rect.top + this.scrollY;
          break;
        case "bottom":
          line.position = rect.bottom + this.scrollY;
          break;
        case "center-y":
          line.position = (rect.top + rect.bottom) / 2 + this.scrollY;
          break;
        case "left":
          line.position = rect.left + this.scrollX;
          break;
        case "right":
          line.position = rect.right + this.scrollX;
          break;
        case "center-x":
          line.position = (rect.left + rect.right) / 2 + this.scrollX;
          break;
      }
    }
  }

  // ── Drag from ruler ───────────────────────────────────

  private bindRulerDrag(canvas: HTMLCanvasElement, axis: "horizontal" | "vertical"): void {
    canvas.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      this.startRulerDrag(e, axis);
    });
  }

  private startRulerDrag(e: PointerEvent, axis: "horizontal" | "vertical"): void {
    const releaseInteractionLock = this.acquireDragInteractionLock();
    const ghost = document.createElement("div");
    ghost.setAttribute("data-string-rulers-ghost", "");
    ghost.setAttribute("data-axis", axis);

    const ghostLabel = document.createElement("span");
    ghostLabel.setAttribute("data-string-rulers-ghost-label", "");
    ghostLabel.setAttribute("data-stdg", "");
    ghost.appendChild(ghostLabel);

    this.linesLayer.appendChild(ghost);
    this.snapEngine.buildCandidates(
      axis,
      this.scrollX,
      this.scrollY,
      this.viewportWidth,
      this.viewportHeight,
      this.currentLines,
      undefined,
      [...this._buildColumnCandidates(axis), ...this._buildRowCandidates(axis)],
    );

    const onMove = (ev: PointerEvent) => {
      const rawDocPos = this.resolveDocPositionFromPointer(axis, ev);

      const snapResult = ev.shiftKey ? null : this.snapEngine.findSnap(rawDocPos);
      const docPos = snapResult
        ? snapResult.position
        : ev.shiftKey
          ? rawDocPos
          : this.snapEngine.snapToStep(rawDocPos);
      if (axis === "horizontal") {
        ghost.style.top = `${docPos}px`;
        ghost.style.left = "0";
        ghost.style.width = `${this.contentWidth}px`;
      } else {
        ghost.style.left = `${docPos}px`;
        ghost.style.top = "0";
        ghost.style.height = `${this.contentHeight}px`;
      }

      if (snapResult) {
        ghost.setAttribute("data-snapping", this._snapCategory(snapResult.type));
        this._setSnapHighlight(snapResult.el ?? null);
        this._setGuideReference(
          snapResult.type === "guide-gap" ? axis : null,
          snapResult.type === "guide-gap" ? (snapResult.referenceLineIds ?? null) : null,
          snapResult.type === "guide-gap" ? (snapResult.gap ?? null) : null,
        );
        ghostLabel.textContent = this._snapLabel(snapResult, docPos, axis);
      } else {
        ghost.removeAttribute("data-snapping");
        this._setSnapHighlight(null);
        this._setGuideReference(null, null, null);
        const vSize = this.getViewportSizeForAxis(axis);
        const scroll = axis === "horizontal" ? this.scrollY : this.scrollX;
        ghostLabel.textContent = String(
          Math.round(this.currentMode.formatPosition(docPos, axis, vSize, scroll)),
        );
      }
    };

    const cleanup = () => {
      releaseInteractionLock();
      ghost.remove();
      this._setSnapHighlight(null);
      this._setGuideReference(null, null, null);
    };

    const onEnd = (ev: PointerEvent) => {
      cleanup();

      const screenPos = this.resolveViewportPointerPos(axis, ev);
      if (screenPos > this.getRulerThickness()) {
        const rawDocPos =
          axis === "horizontal" ? screenPos + this.scrollY : screenPos + this.scrollX;
        const snapResult = ev.shiftKey ? null : this.snapEngine.findSnap(rawDocPos);
        const finalPos = snapResult
          ? snapResult.position
          : ev.shiftKey
            ? rawDocPos
            : this.snapEngine.snapToStep(rawDocPos);
        const binding = this._resolveBinding(snapResult);
        this.callbacks.onLineCreated(
          axis,
          finalPos,
          binding.targetLabel,
          binding.targetSelector,
          binding.targetEdge,
        );
      }
    };

    startPointerDrag(e, { onMove, onEnd, onCancel: cleanup });
  }

  // ── Drag line reposition ──────────────────────────────

  private startLineDrag(e: PointerEvent, line: RulerLine): void {
    const releaseInteractionLock = this.acquireDragInteractionLock();
    const { el } = this.lineEls.get(line.id)!;

    const startPointer = line.axis === "horizontal" ? e.clientY : e.clientX;
    const startDocPosition = line.position;

    this.snapEngine.buildCandidates(
      line.axis,
      this.scrollX,
      this.scrollY,
      this.viewportWidth,
      this.viewportHeight,
      this.currentLines,
      line.id,
      [...this._buildColumnCandidates(line.axis), ...this._buildRowCandidates(line.axis)],
    );

    const onMove = (ev: PointerEvent) => {
      const delta =
        line.axis === "horizontal" ? ev.clientY - startPointer : ev.clientX - startPointer;
      const rawDocPos = Math.max(0, startDocPosition + delta);

      const snapResult = ev.shiftKey ? null : this.snapEngine.findSnap(rawDocPos);
      const docPos = snapResult
        ? snapResult.position
        : ev.shiftKey
          ? rawDocPos
          : this.snapEngine.snapToStep(rawDocPos);

      if (snapResult) {
        el.setAttribute("data-snapping", this._snapCategory(snapResult.type));
        this._setSnapHighlight(snapResult.el ?? null);
        this._setGuideReference(
          snapResult.type === "guide-gap" ? line.axis : null,
          snapResult.type === "guide-gap" ? (snapResult.referenceLineIds ?? null) : null,
          snapResult.type === "guide-gap" ? (snapResult.gap ?? null) : null,
        );
      } else {
        el.removeAttribute("data-snapping");
        this._setSnapHighlight(null);
        this._setGuideReference(null, null, null);
      }

      this.callbacks.onLineMoved(
        line.id,
        docPos,
        snapResult?.el ? this._elementLabel(snapResult.el) : snapResult?.label,
      );

      // positionLineEl resets the label — override it when snapping to a named element
      const entry = this.lineEls.get(line.id);
      if (entry) {
        if (snapResult) {
          entry.labelEl.textContent = this._snapLabel(snapResult, docPos, line.axis);
        } else {
          const vSize = this.getViewportSizeForAxis(line.axis);
          const scroll = line.axis === "horizontal" ? this.scrollY : this.scrollX;
          entry.labelEl.textContent = String(
            Math.round(this.currentMode.formatPosition(docPos, line.axis, vSize, scroll)),
          );
        }
      }
    };

    const cleanup = () => {
      releaseInteractionLock();
      el.removeAttribute("data-snapping");
      this._setSnapHighlight(null);
      this._setGuideReference(null, null, null);
    };

    const onEnd = (ev: PointerEvent) => {
      cleanup();

      const delta =
        line.axis === "horizontal" ? ev.clientY - startPointer : ev.clientX - startPointer;
      const rawDocPos = Math.max(0, startDocPosition + delta);
      const snapResult = ev.shiftKey ? null : this.snapEngine.findSnap(rawDocPos);
      const docPos = snapResult
        ? snapResult.position
        : ev.shiftKey
          ? rawDocPos
          : this.snapEngine.snapToStep(rawDocPos);

      const screenPos = line.axis === "horizontal" ? docPos - this.scrollY : docPos - this.scrollX;

      if (screenPos <= this.getRulerThickness()) {
        this.callbacks.onLineRemoved(line.id);
      } else {
        const binding = this._resolveBinding(snapResult);
        this.callbacks.onDragCommit(
          line.id,
          docPos,
          binding.targetLabel,
          binding.targetSelector,
          binding.targetEdge,
        );
      }
    };

    startPointerDrag(e, { onMove, onEnd, onCancel: cleanup });
  }

  private syncScreenChromePosition(): void {
    const host = this.host;
    const rulerThickness = this.getRulerThickness();
    let chromeWidth = this.viewportWidth;
    let chromeHeight = this.viewportHeight;

    if (!host || host === document.body || host === document.documentElement) {
      this.viewportOffsetX = 0;
      this.viewportOffsetY = 0;
    } else {
      const rect = host.getBoundingClientRect();
      this.viewportOffsetX = Math.round(rect.left);
      this.viewportOffsetY = Math.round(rect.top);
      chromeWidth = Math.max(0, Math.round(rect.width));
      chromeHeight = Math.max(0, Math.round(rect.height));
    }

    this.topCanvas.style.left = `${this.viewportOffsetX}px`;
    this.topCanvas.style.top = `${this.viewportOffsetY}px`;
    this.topCanvas.style.width = `${chromeWidth}px`;
    this.topCanvas.style.height = `${rulerThickness}px`;

    this.leftCanvas.style.left = `${this.viewportOffsetX}px`;
    this.leftCanvas.style.top = `${this.viewportOffsetY}px`;
    this.leftCanvas.style.width = `${rulerThickness}px`;
    this.leftCanvas.style.height = `${chromeHeight}px`;

    this.cornerEl.style.left = `${this.viewportOffsetX}px`;
    this.cornerEl.style.top = `${this.viewportOffsetY}px`;
    this.cornerEl.style.width = `${rulerThickness}px`;
    this.cornerEl.style.height = `${rulerThickness}px`;
  }

  private isCompactMobileRuler(): boolean {
    return getStringDevViewportPolicy(this.viewportWidth).compact;
  }

  private getRulerThickness(): number {
    return this.isCompactMobileRuler() ? RULER_THICKNESS_MOBILE : RULER_THICKNESS;
  }

  private getViewportSizeForAxis(axis: "horizontal" | "vertical"): number {
    return axis === "horizontal" ? this.viewportHeight : this.viewportWidth;
  }

  private resolveViewportPointerPos(axis: "horizontal" | "vertical", ev: PointerEvent): number {
    const clientPos = axis === "horizontal" ? ev.clientY : ev.clientX;
    const offset = axis === "horizontal" ? this.viewportOffsetY : this.viewportOffsetX;
    return clientPos - offset;
  }

  private resolveDocPositionFromPointer(axis: "horizontal" | "vertical", ev: PointerEvent): number {
    const viewportPos = this.resolveViewportPointerPos(axis, ev);
    return axis === "horizontal" ? viewportPos + this.scrollY : viewportPos + this.scrollX;
  }

  private acquireDragInteractionLock(): () => void {
    const release = lockPageInteraction({
      selection: true,
      scroll: this.isCompactMobileRuler(),
      scrollTarget: this.options.scrollTarget,
    });
    const trackedRelease = () => {
      release();
      this.dragInteractionReleases.delete(trackedRelease);
    };
    this.dragInteractionReleases.add(trackedRelease);
    return trackedRelease;
  }

  private releaseAllDragInteractionLocks(): void {
    for (const release of Array.from(this.dragInteractionReleases)) {
      release();
    }
  }
}
